import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { RegionNode, RegionTree } from '@map/shared';
import type { WorldArt } from '@map/worldkit';
import { Globe } from '../globe/Globe.js';
import { SolarSystem } from '../globe/SolarSystem.js';
import { RockSearch, matchesQuery } from './RockSearch.js';
import { loadWorldArt } from '../globe/useWorldLayout.js';
import { CaptureForm } from './CaptureForm.js';
import { Outline } from './Outline.js';
import { PlacePanel } from './PlacePanel.js';
import { StarDialog } from './StarDialog.js';
import { fetchNewsStatus, fetchPlanetBuildings, fetchTasks, fetchTree, fetchUniverse } from './api.js';
import { Newswire, NEWS_STATUS_KEY } from './Newswire.js';
import { TaskPanel } from './TaskPanel.js';
import { Setup } from '../onboarding/Setup.js';
import { TimeTravel } from './TimeTravel.js';
import { AppLinks } from './AppLinks.js';
import { Sheet } from './Sheet.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { healthColor } from '../styles/tokens.js';

export function WorldView() {
  // null means the orbital view: the app opens onto the whole world, never onto
  // a list of what you are behind on.
  const [planetId, setPlanetId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [art, setArt] = useState<WorldArt | null>(null);
  const [artError, setArtError] = useState<string | null>(null);
  const [skippedSetup, setSkippedSetup] = useState(false);
  const [starOpen, setStarOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  // Looking at a day that has passed. Kept here rather than inside the viewer
  // so the live world is unmounted while you are back there: nothing of today
  // is on screen to be mistaken for then, or clicked.
  const [travelling, setTravelling] = useState(false);
  // The flyout opens with the planet: arriving somewhere shows what it is.
  const [panelOpen, setPanelOpen] = useState(false);
  const [unplaced, setUnplaced] = useState<Array<{ id: string; reason: string }>>([]);
  // The surah a camel was clicked on: its plot opens, and it opens inside it.
  const [focusBuildingId, setFocusBuildingId] = useState<string | null>(null);
  const [focusItemId, setFocusItemId] = useState<number | null>(null);

  // A phone gets the same world and a layout that fits in a hand: panels come
  // up as sheets over the globe instead of taking a column beside it.
  const mobile = useIsMobile();
  const universe = useQuery({ queryKey: ['universe'], queryFn: fetchUniverse });
  // The satellite's beacon: cheap, read from what is stored, never starts a scan.
  const newsStatus = useQuery({
    queryKey: NEWS_STATUS_KEY,
    queryFn: fetchNewsStatus,
    refetchInterval: (q) => (q.state.data?.refreshing ? 8000 : 120_000),
    retry: false,
  });

  // Captured work belongs to no planet: it floats in the system view as rock,
  // open and done alike, so nothing captured is ever out of sight.
  const floating = useQuery({
    queryKey: ['floating-tasks'],
    queryFn: () => fetchTasks({ inbox: true, status: 'all' }),
  });

  // The task whose rock (or star) was clicked. Held by id rather than by
  // value, so the panel follows the task as it is edited instead of showing
  // the copy that happened to be on screen when it was opened.
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const openTask = (floating.data?.tasks ?? []).find((t) => t.id === openTaskId) ?? null;

  // Ctrl+F in the system view: only the rocks matching every word stay up.
  const [rockQuery, setRockQuery] = useState('');
  const searched = useMemo(
    () => (floating.data?.tasks ?? []).filter((t) => matchesQuery(t, rockQuery)),
    [floating.data, rockQuery],
  );
  const searchedOpen = useMemo(() => searched.filter((t) => t.status === 'open').length, [searched]);

  // What is adrift, said in words. The rocks are small and far out by design;
  // without a count you cannot tell an empty field from one you have not
  // spotted yet, which is what made capture feel like it had done nothing.
  const adrift = useMemo(
    () => (floating.data?.tasks ?? []).filter((t) => t.status === 'open').length,
    [floating.data],
  );

  const planets = useMemo(
    () => (universe.data?.planets ?? []).filter((p) => !p.archived),
    [universe.data],
  );

  const tree = useQuery({
    queryKey: ['tree', planetId],
    queryFn: () => fetchTree(planetId as string),
    enabled: planetId !== null,
  });

  const buildings = useQuery({
    queryKey: ['planet-buildings', planetId],
    queryFn: () => fetchPlanetBuildings(planetId as string),
    enabled: planetId !== null,
  });

  // What this planet has finished: each one is a boat on its ocean.
  const planetTasks = useQuery({
    queryKey: ['planet-tasks', planetId],
    queryFn: () => fetchTasks({ planetId: planetId as string, status: 'done' }),
    enabled: planetId !== null,
  });

  const artKey = tree.data?.planet.artPlanetKey;

  useEffect(() => {
    if (!artKey) return;
    let cancelled = false;
    setArtError(null);
    loadWorldArt(artKey).then(
      (loaded) => !cancelled && setArt(loaded),
      (err: Error) => !cancelled && setArtError(err.message),
    );
    return () => {
      cancelled = true;
    };
  }, [artKey]);

  const selected = selectedId ? tree.data?.regions[selectedId] : undefined;
  // On a desktop the flyout follows whatever is selected. On a phone it would
  // cover the world on every tap, so browsing only highlights; the panel comes
  // up when it is asked for — by the Details button, or by opening a camel.
  const showPanel = mobile ? panelOpen : selected !== undefined || panelOpen;

  const enterPlanet = (id: string) => {
    setPlanetId(id);
    setSelectedId(null);
    // On a phone the flyout would cover the planet you have just arrived at,
    // so it waits to be asked for.
    setPanelOpen(!mobile);
  };

  // Drill one level deeper than whatever is selected.
  const depth = useMemo(() => {
    if (!selected || !tree.data) return 0;
    let level = 0;
    for (
      let node = selected;
      node.parentId;
      node = tree.data.regions[node.parentId] as typeof node
    ) {
      level += 1;
    }
    return Math.min(level + 1, 3);
  }, [selected, tree.data]);

  if (universe.isLoading) {
    return <div className="flex h-full items-center justify-center text-text-muted">…</div>;
  }

  if (planets.length === 0) {
    if (skippedSetup) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <h1 className="text-xl">An empty universe</h1>
          <p className="max-w-sm text-sm text-text-muted">Nothing here yet, and nothing wrong with that.</p>
          <button
            onClick={() => setSkippedSetup(false)}
            className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm"
          >
            Set one up
          </button>
        </div>
      );
    }
    return <Setup onDone={() => { setSkippedSetup(true); void universe.refetch(); }} />;
  }

  if (travelling) {
    return <TimeTravel onClose={() => setTravelling(false)} />;
  }

  if (!planetId) {
    return (
      <div className="relative h-full">
        <SolarSystem
          planets={planets}
          onEnter={enterPlanet}
          onOpenStar={() => setStarOpen(true)}
          tasks={searched}
          onOpenTask={(task) => setOpenTaskId(task.id)}
          mobile={mobile}
          newsStatus={newsStatus.data}
          onOpenNews={newsStatus.isError ? undefined : () => setNewsOpen(true)}
        />
        <header
          className={
            mobile
              ? 'pointer-events-none absolute inset-x-0 top-0 flex flex-col gap-0.5 px-4 pb-3'
              : 'pointer-events-none absolute inset-x-0 top-0 flex items-center gap-3 p-6'
          }
          style={mobile ? { paddingTop: 'calc(var(--safe-top) + 0.75rem)' } : undefined}
        >
          <h1 className="truncate text-lg">{universe.data?.universe.name ?? 'Your universe'}</h1>
          <span className={mobile ? 'text-sm text-text-muted' : 'text-sm text-text-muted'}>
            {describePlanets(planets)}
            {adrift > 0 && ` · ${adrift} adrift`}
          </span>
          <button
            onClick={() => setTravelling(true)}
            className="pointer-events-auto ml-auto rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm"
          >
            Look back
          </button>
        </header>
        <AppLinks mobile={mobile} />
        <RockSearch query={rockQuery} onQuery={setRockQuery} shown={searchedOpen} total={adrift} />
        <CaptureForm />

        {/* A clicked rock, opened. On a phone it comes up as a sheet over the
            system; on a desktop it takes a column on the right without
            reflowing the canvas, so the rock you clicked stays where it was. */}
        {openTask && !mobile && (
          <div
            className="absolute inset-y-0 right-0 z-20 h-full"
            style={{ width: 'min(24rem, 40vw)', boxShadow: 'var(--shadow-panel)' }}
          >
            <TaskPanel task={openTask} onClose={() => setOpenTaskId(null)} />
          </div>
        )}
        {openTask && mobile && (
          <Sheet label={openTask.title} onClose={() => setOpenTaskId(null)}>
            <TaskPanel task={openTask} onClose={() => setOpenTaskId(null)} mobile />
          </Sheet>
        )}

        {newsOpen && !mobile && (
          <div
            className="absolute inset-y-0 right-0 z-30 h-full"
            style={{ width: 'min(46rem, 64vw)', boxShadow: 'var(--shadow-panel)' }}
          >
            <Newswire onClose={() => setNewsOpen(false)} />
          </div>
        )}
        {newsOpen && mobile && (
          <Sheet label="Newswire" onClose={() => setNewsOpen(false)}>
            <Newswire onClose={() => setNewsOpen(false)} mobile />
          </Sheet>
        )}

        {starOpen && (
          <StarDialog
            universeName={universe.data?.universe.name ?? 'My universe'}
            planets={planets}
            onClose={() => setStarOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-full">
      {outlineOpen && tree.data && !mobile && (
        <div className="h-full shrink-0" style={{ width: 'min(20rem, 34vw)' }}>
          <Outline
            tree={tree.data}
            unplaced={unplaced}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onClose={() => setOutlineOpen(false)}
          />
        </div>
      )}

      {outlineOpen && tree.data && mobile && (
        <Sheet label={`${tree.data.planet.name} outline`} onClose={() => setOutlineOpen(false)}>
          <Outline
            tree={tree.data}
            unplaced={unplaced}
            selectedId={selectedId}
            // Picking a place on a phone means going to it: the list closes and
            // the place opens, rather than both sitting on top of each other.
            onSelect={(id) => {
              setSelectedId(id);
              setOutlineOpen(false);
              setPanelOpen(true);
            }}
            onClose={() => setOutlineOpen(false)}
          />
        </Sheet>
      )}

      {/* min-w-0 matters: a flex item will not shrink below its content's
          intrinsic width by default, and the canvas reports the full viewport,
          so without it the panel is pushed off the side of the screen. */}
      <div className={showPanel && !mobile ? 'h-full min-w-0 flex-1' : 'h-full w-full'}>
        {artError && (
          <div className="flex h-full items-center justify-center text-sm text-text-muted">
            {artError}
          </div>
        )}
        {art && tree.data && !artError && (
          <Globe
            art={art}
            tree={tree.data}
            selectedId={selectedId}
            // Clicking the planet anywhere but a place still opens the flyout —
            // on the planet itself.
            onSelect={(id) => {
              setSelectedId(id);
              if (!mobile) setPanelOpen(true);
              setFocusBuildingId(null);
            }}
            onOpenBuilding={(regionId, buildingId, itemId) => {
              setSelectedId(regionId);
              setPanelOpen(true);
              setFocusBuildingId(buildingId);
              setFocusItemId(itemId ?? null);
            }}
            depth={depth}
            buildings={buildings.data?.buildings ?? []}
            tasks={planetTasks.data?.tasks ?? []}
            onUnplaced={setUnplaced}
          />
        )}
      </div>

      {showPanel && tree.data && !mobile && (
        <div className="h-full shrink-0" style={{ width: 'min(26rem, 42vw)' }}>
          <PlacePanel
            tree={tree.data}
            region={selected ?? null}
            buildings={buildings.data?.buildings ?? []}
            unplaced={unplaced}
            focusBuildingId={focusBuildingId}
            focusItemId={focusItemId}
            onSelect={(id) => {
              setSelectedId(id);
              setPanelOpen(true);
              setFocusBuildingId(null);
            }}
            onClose={() => {
              setSelectedId(null);
              setPanelOpen(false);
              setFocusBuildingId(null);
            }}
          />
        </div>
      )}

      {showPanel && tree.data && mobile && (
        <Sheet
          label={`${selected?.name ?? tree.data.planet.name} details`}
          onClose={() => {
            setSelectedId(null);
            setPanelOpen(false);
          }}
        >
          <PlacePanel
            tree={tree.data}
            region={selected ?? null}
            buildings={buildings.data?.buildings ?? []}
            unplaced={unplaced}
            focusBuildingId={focusBuildingId}
            focusItemId={focusItemId}
            onSelect={(id) => {
              setSelectedId(id);
              setPanelOpen(true);
              setFocusBuildingId(null);
            }}
            onClose={() => {
              setSelectedId(null);
              setPanelOpen(false);
              setFocusBuildingId(null);
            }}
          />
        </Sheet>
      )}

      {mobile ? (
        <header
          className="pointer-events-none absolute inset-x-0 top-0 flex flex-col gap-2 px-3 pb-3"
          style={{ paddingTop: 'calc(var(--safe-top) + 0.5rem)' }}
        >
          <nav
            className="pointer-events-auto flex items-center gap-1 self-start rounded-[var(--radius-md)] p-1"
            style={{ background: 'color-mix(in oklch, var(--surface-1) 88%, transparent)' }}
          >
            <button
              onClick={() => {
                setPlanetId(null);
                setSelectedId(null);
                setPanelOpen(false);
              }}
              className="tap rounded-[var(--radius-sm)] px-3 text-sm text-text-muted"
              aria-label="Back to the whole system"
            >
              ◄
            </button>
            <button
              onClick={() => setOutlineOpen((open) => !open)}
              className="tap rounded-[var(--radius-sm)] px-3 text-sm text-text-muted"
              aria-label="The whole planet as a list"
            >
              ☰
            </button>
            <button
              onClick={() => setPanelOpen((open) => !open)}
              className="tap rounded-[var(--radius-sm)] px-3 text-sm"
              style={{ color: showPanel ? 'var(--text)' : 'var(--text-muted)' }}
            >
              {selected?.name ?? tree.data?.planet.name ?? 'Details'}
            </button>
          </nav>

          {tree.data && (
            <span
              className="pointer-events-none flex max-w-full items-center gap-2 self-start rounded-[var(--radius-sm)] px-2 py-1 text-xs text-text-muted"
              style={{ background: 'color-mix(in oklch, var(--surface-1) 80%, transparent)' }}
            >
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: healthColor(worstHealth(tree.data)) }}
                aria-hidden
              />
              <span className="truncate">
                {selected ? trail(tree.data, selected) : describe(tree.data, buildings.data?.buildings ?? [])}
              </span>
            </span>
          )}
        </header>
      ) : (
      <header className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-3 p-4">
        <nav
          className="pointer-events-auto flex gap-1 rounded-[var(--radius-md)] p-1"
          style={{ background: 'color-mix(in oklch, var(--surface-1) 80%, transparent)' }}
        >
          <button
            onClick={() => {
              setPlanetId(null);
              setSelectedId(null);
              setPanelOpen(false);
            }}
            className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-text-muted hover:text-text"
            title="Back to the whole system"
          >
            ◄ System
          </button>
          <button
            onClick={() => setOutlineOpen((open) => !open)}
            className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-text-muted hover:text-text"
            title="The whole planet as a list"
          >
            ☰ Outline
          </button>
          {planets.map((planet) => {
            const isCurrent = planet.id === planetId;
            return (
              <button
                key={planet.id}
                onClick={() => enterPlanet(planet.id)}
                className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm"
                style={{
                  background: isCurrent ? 'var(--surface-2)' : 'transparent',
                  color: isCurrent ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                {planet.name}
              </button>
            );
          })}
        </nav>

        {tree.data && (
          <span className="pointer-events-none flex items-center gap-2 text-xs text-text-muted">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: healthColor(worstHealth(tree.data)) }}
              aria-hidden
            />
            {/* Where you are beats how you are doing: once you have drilled in,
                the globe on its own does not say which place this is. */}
            {selected ? trail(tree.data, selected) : describe(tree.data, buildings.data?.buildings ?? [])}
          </span>
        )}
      </header>
      )}

      <CaptureForm />
    </div>
  );
}

/** Where you are: planet, then every place you came through to get here. */
function trail(tree: RegionTree, region: RegionNode): string {
  const names: string[] = [];
  for (
    let node: RegionNode | undefined = region;
    node;
    node = node.parentId ? tree.regions[node.parentId] : undefined
  ) {
    names.unshift(node.name);
  }
  return [tree.planet.name, ...names].join('  ›  ');
}

/** MAP-169: the state of the whole universe, in one line. */
function describePlanets(
  planets: Array<{ name: string; health: number; overdueTasks: number; neglect: { burning: number } }>,
): string {
  if (planets.length === 0) return 'nothing here yet';
  // Something acute outranks everything else: it is the one thing that cannot
  // wait. Said the same way whatever it looks like inside the planet.
  const acute = planets.filter((p) => p.neglect.burning > 0);
  if (acute.length > 0) {
    const count = acute.reduce((sum, p) => sum + p.neglect.burning, 0);
    return `▲ ${acute.map((p) => p.name).join(', ')} ${acute.length === 1 ? 'needs' : 'need'} you · ${count} urgent`;
  }
  const worst = planets.reduce((a, b) => (b.health > a.health ? b : a));
  const late = planets.reduce((sum, p) => sum + p.overdueTasks, 0);
  if (worst.health < 0.15) return 'everything is in good order';
  if (worst.health < 0.45) return `${worst.name} could use a look`;
  return `${worst.name} needs attention${late > 0 ? ` · ${late} late` : ''}`;
}

function worstHealth(tree: { regions: Record<string, { health: { combined: number } }> }): number {
  return Math.max(0, ...Object.values(tree.regions).map((r) => r.health.combined));
}

/** MAP-169: one line of state on arrival, not a list of what you are behind on. */
function describe(
  tree: {
    regions: Record<string, { name: string; health: { combined: number }; overdueTasks: number }>;
  },
  buildings: Array<{ name: string; weathering: number }>,
): string {
  const regions = Object.values(tree.regions);
  if (regions.length === 0) return 'an empty world';

  // Past this something is acute, whether it shows as fire, a poisoned river
  // or anything else on the ground.
  const acute = [...buildings].filter((b) => b.weathering >= 0.8).sort((a, b) => b.weathering - a.weathering);
  if (acute.length > 0) {
    const [first] = acute;
    return acute.length === 1
      ? `▲ ${first?.name} needs you`
      : `▲ ${first?.name} and ${acute.length - 1} more need you`;
  }

  const worst = regions.reduce((a, b) => (b.health.combined > a.health.combined ? b : a));
  if (worst.health.combined < 0.15) return 'your world is in good order';
  if (worst.health.combined < 0.45) return `${worst.name} could use a look`;
  return `${worst.name} needs attention`;
}
